import { Fragment, useId, useState, type ReactNode } from 'react';

/**
 * A suggestion: what it puts in the field, and how it is said in the list — its name, what is
 * said beside it paler (`Tallink`), and the group it is listed under (a car's make).
 */
export interface Suggestion {
  value: string;
  label: string;
  detail?: string | undefined;
  group?: string | undefined;
}

/** Where a word typed is found in a text: at the start of one of its words first, else anywhere. */
function found(lower: string, word: string): number {
  let at = lower.indexOf(word);
  const first = at;
  while (at > 0 && !/[\s\-/(.·,]/.test(lower.charAt(at - 1))) {
    at = lower.indexOf(word, at + 1);
  }
  return at < 0 ? first : at;
}

/**
 * A text with each word typed marked where it is found in it: `Tesla M` marks `Tesla` and the
 * `M` of `Model 3`. Unmarked when its letters change in number in lower case.
 */
export function marked(text: string, typed: string): ReactNode {
  const lower = text.toLowerCase();
  if (lower.length !== text.length) return text;
  const spans = typed
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word !== '')
    .map((word) => [found(lower, word), word.length] as const)
    .filter(([at]) => at >= 0)
    .map(([at, length]) => [at, at + length] as const)
    .toSorted((a, b) => a[0] - b[0]);
  const parts: ReactNode[] = [];
  let from = 0;
  for (const [start, end] of spans) {
    if (end <= from) continue;
    const begin = Math.max(start, from);
    parts.push(text.slice(from, begin));
    parts.push(
      <mark key={begin} className="suggest-match">
        {text.slice(begin, end)}
      </mark>,
    );
    from = end;
  }
  parts.push(text.slice(from));
  return parts;
}

export interface SuggestProps {
  label: string;
  /** What is typed. */
  value: string;
  /** What fits what is typed, best first; the parent finds them. */
  suggestions: readonly Suggestion[];
  /** What is typed, or a suggestion's value chosen. */
  onChange: (value: string) => void;
  placeholder?: string;
  /** Offers its suggestions when pressed into empty, as a choice of a few does: the trip's people. */
  openEmpty?: boolean;
  /** How many are offered at most, the list scrolling: eight, a day's half hours all. */
  most?: number;
  /** A button at the field's end: a map to pick the place on. */
  suffix?: ReactNode;
}

/**
 * A text typed freely, with what it may be suggested under it as it is typed (the ARIA combobox
 * pattern, docs/02 §6): arrows move through the list, Enter or a press chooses, Escape closes it;
 * anything else typed stays as typed.
 */
export function Suggest({
  label,
  value,
  suggestions,
  onChange,
  placeholder = '',
  openEmpty = false,
  most = 8,
  suffix,
}: SuggestProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const shown = open ? suggestions.slice(0, most) : [];
  const pick = (suggestion: Suggestion) => {
    onChange(suggestion.value);
    setOpen(false);
    setActive(-1);
  };

  const input = (
    <input
      id={id}
      className="text-input"
      type="text"
      role="combobox"
      autoComplete="off"
      aria-autocomplete="list"
      aria-expanded={shown.length > 0}
      aria-controls={`${id}-list`}
      aria-activedescendant={active >= 0 ? `${id}-${String(active)}` : undefined}
      placeholder={placeholder}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
        setOpen(openEmpty || event.target.value.trim() !== '');
        setActive(-1);
      }}
      // Pressed into with something in it, what fits it shows: a code kept, its airport said.
      onFocus={() => {
        setOpen(openEmpty || value.trim() !== '');
      }}
      onBlur={() => {
        setOpen(false);
        setActive(-1);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && shown.length > 0) {
          event.preventDefault();
          setOpen(false);
          setActive(-1);
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const count = Math.min(suggestions.length, most);
          if (count === 0) return;
          const down = event.key === 'ArrowDown';
          setOpen(true);
          setActive((at) =>
            at < 0 ? (down ? 0 : count - 1) : (at + (down ? 1 : count - 1)) % count,
          );
        } else if (event.key === 'Enter' && shown[active]) {
          event.preventDefault();
          pick(shown[active]);
        }
      }}
    />
  );

  return (
    <div className="field suggest">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      {suffix === undefined ? (
        input
      ) : (
        <div className="input-group">
          {input}
          {suffix}
        </div>
      )}
      <ul
        className="suggest-list"
        id={`${id}-list`}
        role="listbox"
        aria-label={label}
        hidden={shown.length === 0}
      >
        {shown.map((suggestion, at) => (
          <Fragment key={suggestion.value}>
            {/* A group's name over the first of it: a car's make over its models. */}
            {suggestion.group !== undefined && suggestion.group !== shown[at - 1]?.group && (
              <li role="presentation" className="suggest-group">
                {marked(suggestion.group, value)}
              </li>
            )}
            <li
              id={`${id}-${String(at)}`}
              role="option"
              // Said with what is beside it: `Victoria I · Tallink`.
              aria-label={
                suggestion.detail ? `${suggestion.label} · ${suggestion.detail}` : undefined
              }
              aria-selected={at === active}
              className="suggest-option"
              // Chosen before the field loses its focus.
              onMouseDown={(event) => {
                event.preventDefault();
                pick(suggestion);
              }}
            >
              <span className="suggest-label">{marked(suggestion.label, value)}</span>
              {suggestion.detail && <span className="suggest-detail">{suggestion.detail}</span>}
            </li>
          </Fragment>
        ))}
      </ul>
    </div>
  );
}
